import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, catchError, throwError } from "rxjs";
import { ConfigService } from "./config/config.service";
import { ModelExecution } from "../models/executions-fields";
import { FileModel } from "../models/fileData-fields";
import { ProjectService } from "./project/project.service";
import { TcoProjectService } from "./tco-project/tco-project.service";

@Injectable({
  providedIn: "root",
})
export class RestTcoService {
  userId: any = "";
  projectId: any = "";
  userName: any;

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private tcoProjectService: TcoProjectService,
    private projectService: ProjectService
  ) {
    this.tcoProjectService.getTcoUserId().subscribe((item: any) => {
      this.userId = item?.data?.id;
      this.userName = item?.data?.name;
    });
    this.projectService.getProjectId().subscribe((id) => {
      this.projectId = id;
    });
  }

  private get baseUrl(): string {
    return this.configService.getConfig("tcoApiUrl");
  }

  private get pythonUrl(): string {
    return this.configService.getConfig("tcoPythonUrl");
  }

  // Set common headers
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      userId: this.userId,
      "Content-Type": "application/json",
      projectId: this.projectId ? this.projectId : "",
    });
  }

  // Utility for making GET requests
  private get<T>(url: string, params?: any): Observable<T> {
    return this.http
      .get<T>(url, { headers: this.getHeaders(), params })
      .pipe(catchError(this.handleError));
  }

  // Utility for making POST requests
  private post<T>(url: string, body: any): Observable<T> {
    return this.http.post<T>(url, body).pipe(catchError(this.handleError));
  }

  // Utility for making PUT requests
  private put<T>(url: string, body: any): Observable<T> {
    return this.http
      .put<T>(url, body, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError));
  }

  // Error handling
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = "Something went wrong; please try again later.";
    if (error.error instanceof ErrorEvent) {
      errorMessage = `An error occurred: ${error.error.message}`;
    } else {
      errorMessage = error?.error.message;
    }
    return throwError(() => new Error(errorMessage));
  }

  createUsers(payload: any): Observable<any> {
    return this.post<any>(`${this.baseUrl}/users`, payload);
  }

  getPreProcessRecords(): Observable<any> {
    return this.get<any>(`${this.baseUrl}/preprocess`);
  }

  getExecutionReleaseOptions(tenantId: string) {
    const params = new HttpParams()
      .set('tenantId', tenantId)
      .set('projectId', this.projectId);
  
    return this.http.get<string[]>(`${this.baseUrl}/release-testcases/release-ids`, { params });
  }

  getTestCasesByMultipleReleaseIds( tenantId: string, releaseIds: string[]
  ) {
    const params = new HttpParams()
      .set('tenantId', tenantId)
      .set('projectId', this.projectId);
  
    return this.http.post<any>(
      `${this.baseUrl}/release-testcases/by-multiple-releaseIds`, releaseIds, {params}

    );
  }


  uploadReportFile(payload: any): Observable<FileModel> {
    payload.append("userId", this.userId);
    payload.append("projectId", this.projectId);
    return this.post<FileModel>(`${this.baseUrl}/preprocess`, payload);
  }

  downloadFileContent(id: string): Observable<Blob> {
    const url = `${this.baseUrl}/preprocess/files/${id}`;
    return this.http.get(url, { responseType: "blob" });
  }

  processFileContent(payload: any): Observable<any> {
    payload.projectId = this.projectId;
    return this.http.post(`${this.pythonUrl}/step1`, payload);
  }

  updateExecutionStatus(id: string, executionStatus: boolean): Observable<FileModel> {
    return this.put<FileModel>(
      `${this.baseUrl}/preprocess/files/${id}/execution-status?executionStatus=${executionStatus}`,
      {}
    );
  }

  // updateExecutionStatus(
  //   id: string,
  //   executionStatus: boolean
  // ): Observable<FileModel> {
  //   const formData = new FormData();
  //   const statusString = executionStatus?.toString();
  //   console.log("Execution Status ::", executionStatus);
  //   console.log("String Execution Status :", statusString)
  //   formData.append("executionStatus", statusString);
  //   console.log("Form Data :", formData)
  //   return this.put<FileModel>(
  //     `${this.baseUrl}/preprocess/files/${id}/execution-status`,
  //     formData
  //   );
  // }

  getAllMetadataEntries(): Observable<any[]> {
    return this.get<any[]>(`${this.baseUrl}/metadataentry`);
  }

  prosessFileStep2(payload: any): Observable<any> {
    payload.projectId = this.projectId;
    return this.http.post<any>(`${this.pythonUrl}/topic_model`, payload)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
  }

  getAllModels(): Observable<any[]> {
    return this.get<any[]>(`${this.baseUrl}/models`);
  }

  prosessFileStep3(payload: any): Observable<any> {
    payload.projectId = this.projectId;
    return this.post<any>(`${this.pythonUrl}/step3`, payload);
  }

  executepython(requestData: any): Observable<any> {
    requestData.projectId = this.projectId;
    return this.post(`${this.pythonUrl}/resultsoutput`, requestData);
  }

  savemodel(data: any): Observable<any> {
    data.author = this.userName;
    return this.http.post<any[]>(`${this.baseUrl}/modelexecutions`, data, {
      headers: this.getHeaders(),
    });
  }

  getAllModelExecutions(): Observable<ModelExecution[]> {
    return this.get<ModelExecution[]>(`${this.baseUrl}/modelexecutions`);
  }

  sendToUniqueTestCases(id: string, requestBody: any): Observable<any> {
    requestBody.projectId = this.projectId;
    return this.post<any>(`${this.pythonUrl}/results/${id}`, requestBody);
  }

  saveAllSegmentationData(data: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/segmentation/saveAll`, data, {
      headers: this.getHeaders(),
    });
  }

  sendAllTestcases(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/duplicatetestcases/saveAll`,
      data,
      { headers: this.getHeaders() }
    );
  }

  saveAllUniquesInDuplicates(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/uniqueInDuplicatesTestcases`,
      data,
      { headers: this.getHeaders() }
    );
  }

  saveAllInitalRetainTestcases(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/retainedUniqueTestcases/initialData`,
      data,
      { headers: this.getHeaders() }
    );
  }

  sendAllTestcasesOverlappings(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/overlappingtestcases/saveAll`,
      data,
      { headers: this.getHeaders() }
    );
  }

  sendAllTestcasesOverlappingCombinations(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/overlappingCombinations/saveAll`,
      data,
      { headers: this.getHeaders() }
    );
  }

  saveAllTestcases(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/duplicatesInOverlapsCombinations/saveAll`,
      data,
      { headers: this.getHeaders() }
    );
  }

  saveAllDiscardTestcases(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/discardsInOverlapsCombinations/saveAll`,
      data,
      { headers: this.getHeaders() }
    );
  }

  sendAllObsoleteTestcases(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/obsoletetestcases/saveAll`,
      data,
      { headers: this.getHeaders() }
    );
  }

  sendAllUniquetestcase(data: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/uniquetestcases/saveAll`,
      data,
      { headers: this.getHeaders() }
    );
  }

  deleteAllFilesFromDirectory(requestBody: any): Observable<any> {
    requestBody.projectId = this.projectId;
    return this.http.post<any>(`${this.pythonUrl}/step_final`, requestBody);
  }

  getAllAccounts(): Observable<any> {
    return this.get<any>(`${this.baseUrl}/accounts`);
  }

  getCountUniqueInDuplicatesByModelId(modelId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/uniqueInDuplicatesTestcases/count/modelId/${modelId}`
    );
  }

  getAllSegmentationDataByModelId(modelId: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/segmentation/modelId/${modelId}`);
  }

  getAllTestcasesByModelId(modelId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/overlappingtestcases/modelId/${modelId}`
    );
  }

  getAllTestcasesByModel(modelId: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/uniquetestcases/modelId/${modelId}`);
  }

  getAllOverlapTestcasesByModelId(modelId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/overlappingCombinations/modelId/${modelId}`
    );
  }

  getObsoleteTestcasesByModelId(modelId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/obsoletetestcases/modelId/${modelId}`
    );
  }

  getResultsModelById(id: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/result/modelId/${id}`);
  }

  getAllModelExecutionsResults(): Observable<ModelExecution[]> {
    return this.http.get<ModelExecution[]>(`${this.baseUrl}/modelexecutions`);
  }

  updateMetadataEntries(metadataEntries: any[]): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/metadataentry/saveAll`,
      metadataEntries
    );
  }

  deleteModel(id: string) {
    return this.http.delete<void>(`${this.baseUrl}/modelexecutions/${id}`,
      { headers: this.getHeaders() }
    );
  }

  deleteMetaDataField(id: string) {
    return this.http.delete<void>(`${this.baseUrl}/metadataentry/${id}`);
  }


  deleteFiles(id: string) {
    return this.http.delete<void>(`${this.baseUrl}/preprocess/files/${id}`);
  }

  //////////
  getUniqueInDuplicatesTestcases(payload: any): Observable<any> {
    return this.post<any>(
      `${this.baseUrl}/uniqueInDuplicatesTestcases`,
      payload
    );
  }

  getResultByModelId(modelID: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/result/modelId/${modelID}`);
  }

  getObsoleteTestCases(modelID: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/obsoletetestcases/modelId/${modelID}`
    );
  }

  getoverlappingCombinations(modelID: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/overlappingCombinations/modelId/${modelID}`
    );
  }

  getOverlappingTestCases(modelID: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/uniqueInDuplicatesTestcases/count/overalapping/modelId/${modelID}`
    );
  }

  getUniqueTestCases(modelID: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/uniquetestcases/modelId/${modelID}`);
  }

  getsegmentationTestCases(modelID: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/segmentation/modelId/${modelID}`);
  }

  getoverlappingTestcases(modelID: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/overlappingtestcases/modelId/${modelID}`
    );
  }

  getAllDuplicatesTestcasesByModelId(modelId: string): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/duplicatetestcases/modelId/${modelId}`
    );
  }

  getAllDuplicatesSegmentationDataByModelId(modelId: string): Observable<any> {
    return this.get<any>(`${this.baseUrl}/segmentation/modelId/${modelId}`);
  }

  getDuplicatesCountUniqueInDuplicatesByModelId(
    modelId: string
  ): Observable<any> {
    return this.get<any>(
      `${this.baseUrl}/uniqueInDuplicatesTestcases/count/modelId/${modelId}`
    );
  }

  getNewUniquesInDuplicates(
    modelId: string,
    type: string,
    testIdsInCluster: string[]
  ) {
    const requestBody = {
      testIdsInCluster: testIdsInCluster,
      type: type,
    };
    return this.http.post<any>(
      `${this.baseUrl}/retainedUniqueTestcases/getRetainedUniques/modelId/${modelId}`,
      requestBody,
      { headers: this.getHeaders() }
    );
  }
}
